// アプリのページ定義とアクセス権限ロジック
// ユーザーごとに「アクセス可能なページ」をキー配列で管理する
//
// 権限判定:
//   - admin ロール: 常に全ページアクセス可（allowedPages は無視）
//   - その他: allowedPages=null（未設定）の場合は全許可（既存ユーザーの互換性）
//   - allowedPages が配列指定されている場合は、そのキーに含まれるページのみアクセス可
//
// ⚠️ 同期必須: functions/api/_pages.ts と requiredPagesForApi() のルールを変更した場合、
// 同じ変更を functions/api/_pages.ts にも反映すること。
// Cloudflare Workers 環境では src/ を functions/ からimportできないため、二重定義になっている。

export interface PageDef {
  key: string
  label: string
  path: string
  /** admin以外のユーザーに対してデフォルトでチェックを入れるか（新規ユーザー作成時） */
  defaultChecked?: boolean
  /** シークレットページ（金額・経営判断に関わる機密情報を含む）。
      admin は常時アクセス可、それ以外は canViewSecret + secretMode ON のときのみアクセス可。 */
  secret?: boolean
}

export const APP_PAGES: PageDef[] = [
  { key: 'dashboard', label: 'ダッシュボード', path: '/dashboard', defaultChecked: true, secret: true },
  { key: 'table', label: '資金繰り表', path: '/table', defaultChecked: true, secret: true },
  { key: 'input', label: '収支登録', path: '/input', defaultChecked: true },
  { key: 'banks', label: '銀行口座', path: '/banks', defaultChecked: true, secret: true },
  { key: 'masters', label: '取引先・カテゴリ', path: '/masters' },
  { key: 'recurring', label: '定期取引', path: '/recurring' },
  { key: 'debt_reschedule', label: 'リスケ管理', path: '/reschedule', secret: true },
  { key: 'history', label: '取引履歴', path: '/history', secret: true },
  { key: 'trash', label: 'ゴミ箱', path: '/trash', secret: true },
  { key: 'backup', label: 'バックアップ', path: '/backup', secret: true },
  { key: 'settings', label: '設定', path: '/settings' },
]

export const SECRET_PAGE_KEYS = new Set(APP_PAGES.filter((p) => p.secret).map((p) => p.key))

export const PAGE_MAP: Record<string, PageDef> = Object.fromEntries(
  APP_PAGES.map((p) => [p.key, p]),
)

/**
 * ユーザーが特定のページにアクセスできるか判定（allowed_pages のみ）
 * シークレット判定は別途 canAccessPageWithSecret() を使う。
 */
export function canAccessPage(
  role: string | undefined,
  allowedPages: string[] | null | undefined,
  pageKey: string,
): boolean {
  if (role === 'admin') return true
  // allowedPages が未指定（null）は無制限扱い
  if (!allowedPages || allowedPages.length === 0) return true
  return allowedPages.includes(pageKey)
}

/**
 * シークレット判定込みのページアクセス判定。
 * - シークレットページの場合: シークレット権限あり（admin or canViewSecret）なら閲覧可（常時）
 * - 非シークレットページの場合: 従来通り allowed_pages 判定のみ
 *
 * 注: 以前は secretMode ON/OFF トグルがあったが、UI を簡素化するため廃止。
 *     権限有り = 常時閲覧可。取引単位のシークレットは cell_meta.is_secret で個別制御。
 */
export function canAccessPageWithSecret(
  role: string | undefined,
  allowedPages: string[] | null | undefined,
  canViewSecret: boolean | undefined,
  pageKey: string,
): boolean {
  // 基本のページ権限がなければ不可
  if (!canAccessPage(role, allowedPages, pageKey)) return false
  // シークレットページの場合、権限あり（adminは canViewSecret 扱い）なら閲覧可
  if (SECRET_PAGE_KEYS.has(pageKey)) {
    return role === 'admin' || !!canViewSecret
  }
  return true
}

/**
 * API パスから必要なページキー候補を返す（いずれか1つ持っていればOK）
 * ロジック例:
 *   /api/cells → ['table', 'input', 'dashboard'] のいずれか
 *   /api/recurring → ['recurring'] 必須
 */
export function requiredPagesForApi(method: string, pathname: string): string[] {
  // 認証系は権限不問（ミドルウェアで別処理）
  if (pathname.startsWith('/api/auth/')) return []

  // 監査ログは全ユーザーが書き込み可（自分の操作記録）
  if (pathname === '/api/audit_verify') return ['settings']
  if (pathname.startsWith('/api/audit')) return []

  // 読み取り共通（取引先・カテゴリ・銀行・部署 などのマスタ情報は多くのページで必要）
  const isRead = ['GET', 'HEAD', 'OPTIONS'].includes(method)

  if (pathname.startsWith('/api/cell_confirm')) {
    return ['table', 'input']
  }
  if (pathname.startsWith('/api/cells')) {
    return isRead ? ['table', 'input', 'dashboard'] : ['table', 'input']
  }
  if (pathname.startsWith('/api/categories')) {
    return isRead
      ? ['table', 'input', 'masters', 'recurring', 'debt_reschedule', 'dashboard']
      : ['masters', 'input', 'table']  // input画面でも新規カテゴリ作成可
  }
  if (pathname.startsWith('/api/vendors')) {
    return isRead
      ? ['table', 'input', 'masters', 'recurring', 'debt_reschedule', 'dashboard']
      : ['masters', 'input', 'table']  // input画面でも新規取引先作成可
  }
  if (pathname.startsWith('/api/banks')) {
    return isRead ? ['banks', 'table', 'input', 'masters', 'dashboard'] : ['banks', 'masters']
  }
  if (pathname.startsWith('/api/departments')) {
    return isRead ? ['table', 'input', 'masters'] : ['masters']
  }
  if (pathname.startsWith('/api/debts')) return ['debt_reschedule']
  if (pathname.startsWith('/api/reschedules')) {
    return isRead ? ['debt_reschedule', 'dashboard'] : ['debt_reschedule']
  }
  if (pathname.startsWith('/api/recurring')) return ['recurring']
  if (pathname.startsWith('/api/settings')) return ['settings']
  if (pathname.startsWith('/api/users')) return ['settings']
  if (pathname.startsWith('/api/permission_sets')) return ['settings']
  if (pathname.startsWith('/api/cleanup')) return ['settings']
  if (pathname.startsWith('/api/backup')) return ['backup']
  if (pathname.startsWith('/api/import')) return ['backup']

  // 不明なパスは許可しない
  return ['__unknown__']
}
